<script setup lang="ts">
import AdminProjectAndUsers from '@/components/admin/project/AdminProjectAndUsers.vue';
import BulkProvisioningPanel from '@/components/admin/project/BulkProvisioningPanel.vue';
import AdminInviteGuestModal from '@/components/admin/project/details/AdminInviteGuestModal.vue';
import ProjectSharePointCell from '@/components/admin/project/ProjectSharePointCell.vue';
import { isProvisioningActive } from '@/components/admin/project/project-sharepoint-status';
import { useBulkProvisioning } from '@/components/admin/project/use-bulk-provisioning';
import SimpleTable from '@/components/tables/SimpleTable.vue';
import { adminProjectsService } from '@/services/admin/admin-projects-service.ts';
import { overlaySpinnerService } from '@/services/app/overlay-spinner-service';
import { fortnoxUsersService } from '@/services/fortnox-users/fortnox-users-service';
import { notificationService } from '@/services/notification/notification-service';
import { findNotification } from '@/utils/errors/notification-registry';
import type { BaseColumnData } from '@/utils/tables/column-data';
import { sortData } from '@/utils/tables/sorting-data';
import { PhFloppyDisk } from '@phosphor-icons/vue';
import { isClientError, models } from 'cesg-shared';
import type { UserRole } from 'cesg-shared/dist/models';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const { t } = useI18n();
const route = useRoute();

const projects = ref<models.ListAdminProjectsResponse>([]);
const archivedProjects = ref<models.ListAdminProjectsResponse>([]);

const isCreating = ref(false);
const isInviting = ref(false);
const isShowArchived = ref(false);
const newProjectName = ref('');
const newProjectNumber = ref('');
const loading = ref(false);
const firstProjectIndex = 0;

const selectedProjectId = ref<string | null>(null);
const selectedProjectfortnoxId = ref<string | null>(null);
const selectedProjectIsArchived = ref<boolean | null>(null);

const POLL_INTERVAL_MS = 5000;
let pollIntervalId: ReturnType<typeof setInterval> | null = null;
let pollInFlight = false;

/** Writes a fresh status onto whichever list holds the project, leaving every other field alone —
 * polling must not disturb the row the user has selected or the sort order. */
function patchSharePointStatus(
  projectId: string,
  sharePoint: models.ProjectWorkspaceStatus | null,
) {
  for (const list of [projects, archivedProjects]) {
    const project = list.value.find((candidate) => candidate.id === projectId);
    if (project) project.sharePoint = sharePoint;
  }
}

/**
 * The single seam every status update goes through — a manual click, a bulk-loop POST, or a poll
 * tick. Writing the row and re-evaluating the poll must always happen together: the bulk loop only
 * ever observes a project's progress through the rows this poll refreshes.
 */
function applyStatus(
  projectId: string,
  sharePoint: models.ProjectWorkspaceStatus | null,
) {
  patchSharePointStatus(projectId, sharePoint);
  updatePolling();
}

function activeProjectIds(): string[] {
  return [...projects.value, ...archivedProjects.value]
    .filter((project) => isProvisioningActive(project.sharePoint))
    .map((project) => project.id);
}

/**
 * Polls only the projects that are actually provisioning, via the focused status endpoint. Earlier
 * this refetched both full project lists every tick, which scales with the project table rather
 * than with the handful of live runs (the server caps those at three).
 */
async function pollActiveProvisioning() {
  if (pollInFlight) return;
  const ids = activeProjectIds();
  if (ids.length === 0) {
    updatePolling();
    return;
  }

  pollInFlight = true;
  try {
    await Promise.all(
      ids.map(async (projectId) => {
        try {
          patchSharePointStatus(
            projectId,
            await adminProjectsService.getSharePointStatus(projectId),
          );
        } catch (error) {
          // `getSharePointStatus` already maps 404 → `null` (the ledger row is genuinely gone), so
          // anything reaching here is transient and must leave the row alone. Nulling it on any
          // error would reset a live row to "not provisioned" and get it re-queued by the bulk loop.
          console.error(t('adminProjects.sharePointStatusFailed'), error);
        }
      }),
    );
  } finally {
    pollInFlight = false;
    updatePolling();
  }
}

function updatePolling() {
  const active = activeProjectIds().length > 0;

  if (active && pollIntervalId === null) {
    pollIntervalId = setInterval(pollActiveProvisioning, POLL_INTERVAL_MS);
  } else if (!active && pollIntervalId !== null) {
    clearInterval(pollIntervalId);
    pollIntervalId = null;
  }
}

const loadProjects = async (options: { silent?: boolean } = {}) => {
  if (!options.silent) overlaySpinnerService.show();
  try {
    const result: models.ListAdminProjectsResponse =
      await adminProjectsService.getAll();
    const archived: models.ListAdminProjectsResponse =
      await adminProjectsService.getArchived();

    archivedProjects.value = sortData(archived, 'fortnoxId', 'desc');
    projects.value = sortData(result, 'fortnoxId', 'desc');

    const hasProjects = result.length > 0;
    if (hasProjects) {
      const exists = result.find((p) => p.id === selectedProjectId.value);
      if (!exists) {
        selectedProjectId.value = result[firstProjectIndex].id;
        selectedProjectfortnoxId.value = result[firstProjectIndex].fortnoxId;
      }
    } else {
      selectedProjectId.value = null;
      selectedProjectfortnoxId.value = null;
    }
  } catch (error) {
    console.error(t('adminProjects.loadProjectFailed'), error);
  } finally {
    if (!options.silent) overlaySpinnerService.hide();
  }
  updatePolling();
};

async function provisionSharePoint(projectId: string) {
  try {
    // The 202 body *is* the current status, so the chip updates from the response itself — no
    // refetch. A failed call intentionally leaves the chip exactly as it was.
    applyStatus(
      projectId,
      await adminProjectsService.provisionSharePointSite(projectId),
    );
  } catch (error) {
    // D22's 429 is backpressure, not a failure, and now carries its own message — the generic
    // "request failed" toast used to be all the operator got (slice-1 deviation 4).
    if (isClientError(error, 'SHAREPOINT_PROVISIONING_BUSY')) {
      notificationService.open(
        findNotification(error) ?? {
          text: t('adminProjects.sharePointProvisionFailed'),
          type: 'warning',
        },
      );
      return;
    }
    console.error(t('adminProjects.sharePointProvisionFailed'), error);
  }
}

const bulk = useBulkProvisioning({ projects, applyStatus });

/** Cleared when a new run starts, so the panel never shows a stale summary. */
const bulkSummaryDismissed = ref(false);

function projectsByIds(ids: string[]): models.ListAdminProjectsResponse {
  return ids.flatMap((id) =>
    projects.value.filter((project) => project.id === id),
  );
}

const bulkFailedProjects = computed(() => projectsByIds(bulk.failedIds.value));
const bulkDeferredProjects = computed(() =>
  projectsByIds(bulk.deferredIds.value),
);
const showBulkSummary = computed(
  () => bulk.phase.value === 'done' && !bulkSummaryDismissed.value,
);

function startBulkProvisioning() {
  bulkSummaryDismissed.value = false;
  void bulk.start();
}

async function createProject() {
  if (!newProjectName.value || !newProjectNumber.value) return;

  loading.value = true;
  try {
    const createdProject = await adminProjectsService.createProject(
      newProjectNumber.value.toString(),
      newProjectName.value.toString(),
    );

    projects.value.unshift(...createdProject);

    newProjectName.value = '';
    newProjectNumber.value = '';
    isCreating.value = false;

    await loadProjects();
  } catch (error) {
    console.error(t('adminProjects.createProjectFailed'), error);
  } finally {
    loading.value = false;
  }
}

async function inviteGuest(guestDetails: {
  email: string;
  name: string;
  role: UserRole;
}) {
  if (!guestDetails.email || !guestDetails.name || !guestDetails.role) {
    return;
  }

  try {
    await fortnoxUsersService.createGuestUser(guestDetails);
    await loadProjects();
  } catch (error) {
    console.error(t('adminProjects.inviteFailed'), error);
  }
}

function selectProject(id: string, fortnoxId: string, isArchived: boolean) {
  selectedProjectId.value = id;
  selectedProjectfortnoxId.value = fortnoxId;
  selectedProjectIsArchived.value = isArchived;
}
onMounted(loadProjects);

onUnmounted(() => {
  bulk.stop();
  if (pollIntervalId !== null) {
    clearInterval(pollIntervalId);
    pollIntervalId = null;
  }
});

watch(
  () => route.params.id,
  (newId) => {
    if (newId) loadProjects();
  },
);

const columns: BaseColumnData[] = [
  {
    key: t('adminProjects.number'),
    label: t('adminProjects.projectNumber'),
    sortable: true,
  },
  {
    key: t('adminProjects.name'),
    label: t('adminProjects.labelName'),
    sortable: true,
  },
  {
    key: 'sharePoint',
    label: t('adminProjects.sharePointColumn'),
    sortable: false,
  },
];
</script>

<template>
  <div class="columns">
    <div class="column is-one-third">
      <div class="new-project">
        <div class="column is-narrow p-0 is-creating" v-show="isCreating">
          <input v-model="newProjectNumber" placeholder="Projektnummer" />
          <input v-model="newProjectName" placeholder="Projektnamn" />
          <button @click="createProject" :disabled="loading">
            <PhFloppyDisk size="22" weight="bold" />
          </button>
        </div>

        <button
          class="button is-primary is-small mt-2"
          @click="isCreating = true"
          v-show="!isCreating"
        >
          {{ t('adminProjects.addProject') }}
        </button>
        <button
          class="button is-primary is-small mt-2"
          @click="isInviting = true"
          v-show="!isInviting"
        >
          {{ t('adminProjects.inviteUser') }}
        </button>
      </div>
      <BulkProvisioningPanel
        :pending-count="bulk.pendingCount.value"
        :is-running="bulk.isRunning.value"
        :is-stopping="bulk.isStopping.value"
        :phase="bulk.phase.value"
        :total="bulk.total.value"
        :settled="bulk.settled.value"
        :ready-count="bulk.readyIds.value.length"
        :failed-projects="bulkFailedProjects"
        :deferred-projects="bulkDeferredProjects"
        :show-summary="showBulkSummary"
        @start="startBulkProvisioning"
        @stop="bulk.stop()"
        @provision="provisionSharePoint"
        @dismiss="bulkSummaryDismissed = true"
      />
      <SimpleTable :columns="columns" :data="projects" :enable-search="true">
        <template #tbody="{ data }">
          <tr
            v-for="project in data"
            :key="project.id"
            @click="selectProject(project.id, project.fortnoxId, false)"
            :class="{ active: selectedProjectId === project.id }"
            style="cursor: pointer"
          >
            <td v-for="(column, index) in columns" :key="index">
              <ProjectSharePointCell
                v-if="column.key === 'sharePoint'"
                :share-point="project.sharePoint"
                @provision="provisionSharePoint(project.id)"
              />
              <template v-else>
                {{ project[column.key] }}
              </template>
            </td>
          </tr>
        </template>
      </SimpleTable>

      <div class="level is-vcentered title is-5 mb-4">
        <div
          class="level-left"
          @click="isShowArchived = !isShowArchived"
          style="cursor: pointer"
        >
          <p>{{ t('adminProjects.archives') }}</p>
          <span class="icon is-small">
            {{ isShowArchived ? '▼' : '▶' }}
          </span>
        </div>
      </div>
      <div v-show="isShowArchived" id="archived-projects">
        <SimpleTable
          :columns="columns"
          :data="archivedProjects"
          :enable-search="true"
        >
          <template #tbody="{ data }">
            <tr
              v-for="project in data"
              :key="project.id"
              @click="selectProject(project.id, project.fortnoxId, true)"
              :class="{ active: selectedProjectId === project.id }"
              style="cursor: pointer"
            >
              <td v-for="(column, index) in columns" :key="index">
                <ProjectSharePointCell
                  v-if="column.key === 'sharePoint'"
                  :share-point="project.sharePoint"
                  @provision="provisionSharePoint(project.id)"
                />
                <template v-else>
                  {{ project[column.key] }}
                </template>
              </td>
            </tr>
          </template>
        </SimpleTable>
      </div>
    </div>

    <div class="column">
      <div class="sticky-wrapper">
        <AdminProjectAndUsers
          :projectId="selectedProjectId"
          :fortnoxId="selectedProjectfortnoxId"
          :isArchived="selectedProjectIsArchived"
          @reloadProjects="loadProjects"
        />
      </div>
    </div>
  </div>

  <AdminInviteGuestModal
    v-if="isInviting"
    @close="isInviting = false"
    @invite="inviteGuest"
  />
</template>

<style scoped lang="scss">
.active {
  background-color: var(--admin-projects-active) !important;
}

.column:last-child {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 300px;
}

.sticky-wrapper {
  position: sticky;
  top: 100px;
  align-self: flex-start;
  width: 100%;
}

.new-project {
  display: flex;
  gap: 0.5rem;
  margin: 0.5rem 0;
  align-items: center;
  min-height: 42px;

  input {
    flex: 1;
    padding: 0.45rem 0.6rem;
    border-radius: 6px;
    border: 1px solid #c0d4f7;
    font-size: 1rem;
    box-sizing: border-box;
    height: 42px;
    margin-top: 0;
  }

  .is-creating :nth-last-child(2) {
    margin-left: 5px;
  }

  button {
    background-color: var(--admin-projects-add-user);
    color: rgba(0, 0, 0, 0.8);
    border: 1px solid #333;

    border-radius: 6px;
    padding: 0 1.2rem;
    height: 42px;
    font-size: 1rem;
    font-weight: 600;
    margin: 0.5rem 0 1rem;

    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;

    cursor: pointer;
    user-select: none;
    transition: all 0.2s ease-in-out;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &:hover {
      background-color: var(--admin-projects-add-user-hover);
      border-color: #000;
      box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    }

    &:active {
      border-color: #333;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }
  }
}
</style>
